/**
 * Business hours counter utility
 * Counts 24h/day, Mon-Fri only (weekends excluded)
 * 72h = 3 weekdays
 */

/** Get the counter start time.
 *  Weekend → next Monday 00:00.
 *  Otherwise → exact creation time. */
export function getCounterStart(createdAt) {
  const date = new Date(createdAt);
  const day = date.getDay(); // 0=Sun, 6=Sat

  // Weekend → next Monday 00:00
  if (day === 0 || day === 6) {
    const next = new Date(date);
    while (next.getDay() === 0 || next.getDay() === 6) {
      next.setDate(next.getDate() + 1);
    }
    next.setHours(0, 0, 0, 0);
    return next;
  }

  // Weekday → start immediately
  return date;
}

/** Calculate hours between two dates counting only Mon-Fri, 24h/day */
export function calculateBusinessHours(start, end) {
  if (!start || !end || end <= start) return 0;

  let totalMinutes = 0;
  const current = new Date(start);

  while (current < end) {
    const day = current.getDay();

    // Skip weekends
    if (day === 0 || day === 6) {
      current.setDate(current.getDate() + 1);
      current.setHours(0, 0, 0, 0);
      continue;
    }

    // Weekday: count from current to end-of-day (midnight) or end
    const dayEnd = new Date(current);
    dayEnd.setDate(dayEnd.getDate() + 1);
    dayEnd.setHours(0, 0, 0, 0);

    // If dayEnd falls on Saturday, skip to Monday
    const effectiveEnd = end < dayEnd ? new Date(end) : dayEnd;

    if (current < effectiveEnd) {
      totalMinutes += (effectiveEnd - current) / (1000 * 60);
    }

    // Move to next day midnight
    current.setDate(current.getDate() + 1);
    current.setHours(0, 0, 0, 0);
  }

  return totalMinutes / 60;
}

/** Calculate paused business-hours from `validation_pauses` array, clamped to [start, end].
 *  Each pause object: { paused_at, resumed_at, reason, resolved_by }.
 *  A pause without `resumed_at` is considered still open and is clamped to `end`.
 *  Returns total paused business hours. */
export function calculatePausedBusinessHours(pauses, start, end) {
  if (!pauses || !pauses.length || !start || !end) return 0;
  let total = 0;
  for (const p of pauses) {
    if (!p || !p.paused_at) continue;
    const pa = new Date(p.paused_at);
    const pr = p.resumed_at ? new Date(p.resumed_at) : new Date(end);
    const segStart = pa > start ? pa : start;
    const segEnd = pr < end ? pr : end;
    if (segEnd > segStart) {
      total += calculateBusinessHours(segStart, segEnd);
    }
  }
  return total;
}

/** Check if a 3D validation pause is currently open on the order */
export function hasOpenValidationPause(order) {
  const pauses = (order && order.validation_pauses) || [];
  return pauses.some(p => p && p.paused_at && !p.resumed_at);
}

/** Get production threshold in business hours for a given order.
 *  Default: 72h (3 business days). Integral embouts: 120h (5 business days).
 */
export function getOrderThresholdHours(order) {
  const opts = (order && order.options) || {};
  if (opts.embout_type === 'integral') return 120;
  return 72;
}

/** Get elapsed business hours for an order */
export function getOrderBusinessHours(order) {
  const isFinished = order.status === 'completed' || order.status === 'cancelled';
  const hasStlFiles = order.stl_files && order.stl_files.length > 0;

  let timerStart = order.timer_started_at || order.created_at;
  if (!timerStart) return null;

  // Moulage mode: if no STL uploaded AND no timer_started_at set,
  // timer starts 30h after creation
  if (!order.timer_started_at && !hasStlFiles && order.created_at) {
    const created = new Date(order.created_at);
    created.setTime(created.getTime() + 30 * 60 * 60 * 1000);
    timerStart = created.toISOString();
  }

  const start = getCounterStart(timerStart);

  let end;
  if (isFinished && order.timer_stopped_at) {
    end = new Date(order.timer_stopped_at);
  } else if (isFinished && order.completed_at) {
    end = new Date(order.completed_at);
  } else if (isFinished) {
    end = new Date(timerStart);
  } else {
    end = new Date();
  }

  // If start is in the future (moulage not yet "received"), hours = 0
  if (end < start) {
    return { hours: 0, isFinished, hasStl: hasStlFiles, isMoulage: !hasStlFiles && !order.timer_started_at, threshold: getOrderThresholdHours(order) };
  }

  const hours = calculateBusinessHours(start, end);
  // Subtract 3D-validation paused intervals (clamped to [start, end])
  const paused = calculatePausedBusinessHours(order.validation_pauses, start, end);
  const effective = Math.max(0, hours - paused);
  const isPaused = !isFinished && hasOpenValidationPause(order);
  return {
    hours: Math.round(effective * 10) / 10,
    isFinished,
    hasStl: !!order.timer_started_at || hasStlFiles,
    isMoulage: !hasStlFiles && !order.timer_started_at,
    threshold: getOrderThresholdHours(order),
    isPaused,
    pausedHours: Math.round(paused * 10) / 10,
  };
}

/** Format hours for display */
export function formatBusinessHours(hours) {
  if (hours < 1) return `${Math.round(hours * 60)}min`;
  if (hours < 10) return `${hours.toFixed(1)}h`;
  return `${Math.round(hours)}h`;
}

/** Get urgency level based on time elapsed.
 *  Accepts an optional threshold (default 72h). For integral embouts, threshold is 120h.
 *  Visual stages: green < 33%, yellow 33-66%, orange 66-100%, red >= 100% of threshold.
 */
export function getUrgencyLevel(hours, isFinished, threshold = 72) {
  if (isFinished) return 'finished';
  if (hours >= threshold) return 'overdue';
  if (hours >= threshold * (2 / 3)) return 'critical';
  if (hours >= threshold / 3) return 'warning';
  return 'ok';
}
